import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useGoogleLogin } from '@react-oauth/google';
import {
  BASE_URL_SANDBOX,
  BASE_URL_FOR_ELASTIC_SEARCH,
  LOGIN_CUSTOMER_NEW,
  GOOGLE_LOGIN_CUSTOMER,
  REGISTER_CUSTOMER_NEW,
  ADD_DELIVERY_ADDRESS,
} from '../../../Constants/Config';
import { US_STATES } from '../../Header/MiniHeader/US_STATES';
import stateMap from '../../Header/MiniHeader/usStates';
import eventEmitter from '../../Home/eventEmitter';
import {
  setAuthUser,
  setAuthentication,
  setGuestLogin,
} from '../../../redux/features/Authentication/Customer/authUserSlice';
import UseCookieStorage from '../../../Constants/UseCookieStorage';
import { getAddressSuggestionValidationMessage } from '../../../utils/placeSuggestions';

const useAuthModalLogic = (
  onClose,
  setActiveTab,
  postLoginStoreType = '',
  postLoginCategoryName = '',
  onPostLoginCategoryNavigate,
) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const normalizedPostLoginStoreType =
    postLoginStoreType === 'delivery' || postLoginStoreType === 'pickup'
      ? postLoginStoreType
      : '';

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  const [loginValues, setLoginValues] = useState({
    email: '',
    password: '',
    errors: { email: '', password: '' },
  });

  const [registerValues, setRegisterValues] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    password: '',
    confirmpassword: '',
    errors: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      password: '',
      confirmpassword: '',
    },
  });

  // Address autocomplete state for signup
  const addressTimeoutRef = useRef(null);
  const [addressSearchInput, setAddressSearchInput] = useState('');
  const [addressPlaces, setAddressPlaces] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressError, setAddressError] = useState('');

  // reCAPTCHA state
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState('');

  const EmailReg = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  const emoji =
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{1FAB0}-\u{1FABF}\u{1FAC0}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B50}]/gu;
  const Space = /^[^\s]+$/i;
  const PhoneNo = /^\d{10}$/;
  const Nameval = /^\s*[a-zA-Z]+(\s+[a-zA-Z]+)*\s*$/;
  const PassUppercase = /^(?=.*[A-Z])/;
  const PassLowercase = /^(?=.*[a-z])/;
  const PassSpecCha = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/i;
  const PassNum = /\d/i;
  const PassLeng = /^.{8,}$/i;

  const getPasswordValidationError = (value) => {
    if (value === '') {
      return 'Please enter password';
    }

    if (emoji.test(value)) {
      return 'Emoji not allowed';
    }

    if (!PassUppercase.test(value)) {
      return 'Must contain uppercase letter';
    }

    if (!PassLowercase.test(value)) {
      return 'Must contain lowercase letter';
    }

    if (!PassNum.test(value)) {
      return 'Must contain number';
    }

    if (!PassSpecCha.test(value)) {
      return 'Must contain special character';
    }

    if (!PassLeng.test(value)) {
      return 'Must be at least 8 characters';
    }

    if (!Space.test(value)) {
      return 'Space is not allowed';
    }

    return '';
  };

  // Address helper functions
  const extractAddressDetailsForFetchCordinates = (formattedAddress) => {
    const parts = formattedAddress.split(',').map((part) => part.trim());
    if (parts.length < 3) throw new Error('Please fill proper address');
    let state = '', city = '', streetAddress = '', zipCode = '';
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      const zipMatch = part.match(/\b\d{5}\b/);
      if (zipMatch) {
        zipCode = zipMatch[0];
        const statePart = part.replace(zipCode, '').trim();
        state = statePart.length === 2 ? statePart.toUpperCase() : stateMap[statePart];
        if (i > 0) city = parts[i - 1];
        if (i > 1) streetAddress = parts.slice(0, i - 1).join(', ');
        break;
      } else if (US_STATES.includes(part.length === 2 ? part.toUpperCase() : stateMap[part])) {
        state = part.length === 2 ? part.toUpperCase() : stateMap[part];
        if (i > 0) city = parts[i - 1];
        if (i > 1) streetAddress = parts.slice(0, i - 1).join(', ');
        break;
      }
    }
    return { state, zipCode, city, streetAddress };
  };

  const isValidAddress = (addressObj) =>
    addressObj &&
    addressObj.state &&
    US_STATES.includes(addressObj.state) &&
    addressObj.city &&
    addressObj.streetAddress &&
    /[a-zA-Z]/.test(addressObj.streetAddress);

  // Build a human-readable label for a suggestion from the elasticsearch response
  const getSuggestionText = (place) => {
    const structured = place?.placePrediction?.structuredFormat;
    const mainText = structured?.mainText?.text;
    const secondaryText = structured?.secondaryText?.text;

    if (mainText || secondaryText) {
      return [mainText, secondaryText].filter(Boolean).join(', ');
    }

    // Fallback for older payloads
    return place?.placePrediction?.text?.text || '';
  };

  const hasStreetLikeText = (place) => {
    const structured = place?.placePrediction?.structuredFormat;
    const mainText = structured?.mainText?.text || '';
    return /[a-zA-Z]/.test(mainText);
  };

  const resolveZipCodeFromCoordinates = async (latitude, longitude) => {
    if (latitude == null || longitude == null) return '';

    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
      );

      if (!response.ok) return '';

      const geoData = await response.json();
      return geoData?.postcode || '';
    } catch {
      return '';
    }
  };

  const normalizeAddressKey = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const getAddressDedupKey = (address) => {
    const placeId = normalizeAddressKey(address?.placeID || address?.placeId || '');
    if (placeId) return `place:${placeId}`;

    const line = normalizeAddressKey(
      address?.a_address_line_1 || address?.displayAddress || address?.streetAddress || '',
    );
    const city = normalizeAddressKey(address?.a_city || address?.city || '');
    const state = normalizeAddressKey(address?.a_state || address?.state || '');
    const zip = normalizeAddressKey(address?.a_zip || address?.zipCode || '');

    const composite = [line, city, state, zip].filter(Boolean).join('|');
    if (composite) return `addr:${composite}`;

    const uniqueId = address?.unique_id || address?.id;
    return uniqueId ? `id:${uniqueId}` : '';
  };

  const mergeAndDedupeAddresses = (newAddress, existingAddresses = []) => {
    const seen = new Set();

    return [newAddress, ...existingAddresses]
      .filter((address) => {
        const key = getAddressDedupKey(address);
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((address, index) => ({
        ...address,
        is_default: index === 0 ? '1' : '0',
      }));
  };

  const filterValidSuggestions = (suggestions) =>
    suggestions.filter((s) => {
      if (!hasStreetLikeText(s)) return false;
      const fullText = getSuggestionText(s);
      try {
        const parsed = extractAddressDetailsForFetchCordinates(fullText);
        return isValidAddress(parsed);
      } catch {
        return false;
      }
    });

  const fetchAddressSuggestions = useCallback(async (input) => {
    setAddressError('');
    try {
      const response = await axios.get(
        `${BASE_URL_FOR_ELASTIC_SEARCH}places/autocomplete?input=${input}`,
      );
      if (response.status === 200 && response.data?.suggestions?.length > 0) {
        const filtered = filterValidSuggestions(response.data.suggestions);
        setAddressPlaces(filtered);
        if (filtered.length === 0) {
          setAddressError('Please enter a complete street address, not just a ZIP code.');
        }
      } else {
        setAddressError('Please enter a valid US address.');
        setAddressPlaces([]);
      }
    } catch {
      setAddressError('Error fetching location. Please try again.');
    } finally {
      setAddressLoading(false);
    }
  }, []);

  const handleAddressInputChange = useCallback(
    (e) => {
      const filtered = e.target.value.replace(/[^a-zA-Z0-9,# ]/g, '');
      setAddressSearchInput(filtered);
      setSelectedAddress(null);
      setAddressError('');
      if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
      if (filtered) {
        setAddressLoading(true);
        addressTimeoutRef.current = setTimeout(() => fetchAddressSuggestions(filtered), 500);
      } else {
        setAddressPlaces([]);
        setAddressLoading(false);
        setAddressError('');
      }
    },
    [fetchAddressSuggestions],
  );

  const handleAddressPlaceSelect = useCallback(async (place) => {
    const placeId = place.placePrediction?.placeId;
    const description = getSuggestionText(place);
    setAddressPlaces([]);
    setAddressSearchInput(description);

    const validationMessage = getAddressSuggestionValidationMessage(place);
    if (validationMessage) {
      setAddressError(validationMessage);
      return;
    }

    if (!placeId) return;

    try {
      const response = await axios.get(
        `${BASE_URL_FOR_ELASTIC_SEARCH}places/get_details?input=${placeId}`,
      );
      if (response.status === 200) {
        const locationDetails = response.data;
        const { location } = locationDetails || {};
        const structured = place?.placePrediction?.structuredFormat;
        const structuredAddressText = [
          structured?.mainText?.text,
          structured?.secondaryText?.text,
        ]
          .filter(Boolean)
          .join(', ');
        const formattedAddress =
          locationDetails?.formattedAddress || structuredAddressText || description;
        const parsedAddress = extractAddressDetailsForFetchCordinates(formattedAddress);
        const resolvedZipCode =
          parsedAddress.zipCode ||
          (await resolveZipCodeFromCoordinates(
            location?.latitude ?? structured?.lat,
            location?.longitude ?? structured?.lng,
          ));

        if (!parsedAddress.streetAddress || !parsedAddress.city || !parsedAddress.state) {
          setAddressError('Please select a complete street address.');
          return;
        }

        if (!resolvedZipCode) {
          setAddressError('Please select an address with a ZIP code.');
          return;
        }

        setAddressSearchInput(formattedAddress);
        setSelectedAddress({
          streetAddress: formattedAddress,
          displayAddress: formattedAddress,
          city: parsedAddress.city,
          state: parsedAddress.state,
          zipCode: resolvedZipCode,
          latitude: location?.latitude ?? structured?.lat,
          longitude: location?.longitude ?? structured?.lng,
          placeId,
        });

        setAddressSearchInput(formattedAddress);
        setAddressError('');
      }
    } catch {
      setAddressError('Please enter a valid address.');
    }
  }, []);

  const saveAddressToBackend = async (addressData, userData) => {
    try {
      const resolvedZipCode =
        addressData.zipCode ||
        (await resolveZipCodeFromCoordinates(
          addressData.latitude,
          addressData.longitude,
        ));

      if (!resolvedZipCode) {
        setAddressError('Please select an address with a ZIP code.');
        return;
      }

      const normalizedAddressData = {
        ...addressData,
        zipCode: resolvedZipCode,
      };

      const payload = {
        userId: userData?.id || userData?.customer_id,
        address_id: '',
        cc_f_name: userData?.f_name || userData?.firstname || registerValues.firstname,
        cc_l_name: userData?.l_name || userData?.lastname || registerValues.lastname,
        cc_phone_no: userData?.phone || registerValues.phone,
        cc_street_address: normalizedAddressData.displayAddress || normalizedAddressData.streetAddress,
        cc_zip_code: normalizedAddressData.zipCode,
        city: normalizedAddressData.city,
        suite_address: '',
        state: normalizedAddressData.state,
        latitude: normalizedAddressData.latitude,
        longitude: normalizedAddressData.longitude,
        placeID: normalizedAddressData.placeId,
        a_country: 'US',
      };

      const response = await axios.post(
        BASE_URL_SANDBOX + ADD_DELIVERY_ADDRESS,
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response?.data?.status === 200) {
        const newAddressId = response?.data?.result?.id;
        // Save to cookies as well
        const coords = { lat: normalizedAddressData.latitude, lng: normalizedAddressData.longitude };
        Cookies.set('userLocation', JSON.stringify(coords), { expires: 7 });
        Cookies.set('zipCode', JSON.stringify(normalizedAddressData.zipCode), { expires: 7 });

        const newAddress = {
          unique_id: newAddressId || Date.now(),
          a_address_line_1: normalizedAddressData.displayAddress || normalizedAddressData.streetAddress,
          displayAddress: normalizedAddressData.displayAddress || normalizedAddressData.streetAddress,
          a_zip: normalizedAddressData.zipCode,
          a_city: normalizedAddressData.city,
          a_state: normalizedAddressData.state,
          latitude: normalizedAddressData.latitude,
          longitude: normalizedAddressData.longitude,
          placeID: normalizedAddressData.placeId,
          is_default: '1',
        };
        const existing = Cookies.get('customerAddresses');
        let addressArray = [newAddress];
        if (existing) {
          try {
            const parsed = JSON.parse(existing);
            if (Array.isArray(parsed)) {
              addressArray = mergeAndDedupeAddresses(newAddress, parsed);
            }
          } catch {}
        }
        Cookies.set('customerAddresses', JSON.stringify(addressArray), { expires: 7 });
        eventEmitter.emit('locationUpdated');
      }
    } catch {}
  };

  const handleLoginSuccess = async (userData) => {
    try {
      localStorage.removeItem('is_guest');
    } catch {}
    Cookies.remove('is_guest', { path: '/' });
    dispatch(setGuestLogin(false));
    dispatch(setAuthUser(userData));
    dispatch(setAuthentication(true));
    onClose();
    if (postLoginCategoryName && onPostLoginCategoryNavigate) {
      await onPostLoginCategoryNavigate(postLoginCategoryName);
      return;
    }
    navigate(
      normalizedPostLoginStoreType
        ? `/home?store=${normalizedPostLoginStoreType}`
        : '/home',
      { replace: false },
    );
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    let errors = { ...loginValues.errors };

    if (errorMessage) setErrorMessage('');

    switch (name) {
      case 'email':
        if (value === '') {
          errors.email = 'Please enter email';
        } else if (emoji.test(value)) {
          errors.email = 'Emoji not allowed';
        } else if (!EmailReg.test(value)) {
          errors.email = 'Please enter valid email';
        } else {
          errors.email = '';
        }
        break;
      case 'password':
        if (value === '') {
          errors.password = 'Please enter password';
        } else if (emoji.test(value)) {
          errors.password = 'Emoji not allowed';
        } else if (!Space.test(value)) {
          errors.password = 'Space is not allowed';
        } else {
          errors.password = '';
        }
        break;
      default:
        break;
    }

    setLoginValues((prev) => ({
      ...prev,
      [name]: value,
      errors,
    }));
  };

  const handleRegisterInputChange = (e) => {
    const { name, value } = e.target;
    let errors = { ...registerValues.errors };
    const fieldValue = name === 'phone' ? value.replace(/[^0-9]/g, '') : value;

    if (errorMessage) setErrorMessage('');

    switch (name) {
      case 'firstname':
        if (fieldValue === '') {
          errors.firstname = 'Please enter First Name';
        } else if (fieldValue.length > 50) {
          errors.firstname = 'First Name cannot exceed 50 characters';
        } else if (emoji.test(fieldValue)) {
          errors.firstname = 'Emoji not allowed';
        } else if (!Nameval.test(fieldValue)) {
          errors.firstname = 'Alphabets only';
        } else {
          errors.firstname = '';
        }
        break;
      case 'lastname':
        if (fieldValue === '') {
          errors.lastname = '';
        } else if (fieldValue.length > 50) {
          errors.lastname = 'Last Name cannot exceed 50 characters';
        } else if (emoji.test(fieldValue)) {
          errors.lastname = 'Emoji not allowed';
        } else if (!Nameval.test(fieldValue)) {
          errors.lastname = 'Alphabets only';
        } else {
          errors.lastname = '';
        }
        break;
      case 'email':
        if (fieldValue === '') {
          errors.email = 'Please enter email';
        } else if (emoji.test(fieldValue)) {
          errors.email = 'Emoji not allowed';
        } else if (!EmailReg.test(fieldValue)) {
          errors.email = 'Please enter valid email';
        } else {
          errors.email = '';
        }
        break;
      case 'phone':
        if (fieldValue === '') {
          errors.phone = 'Please enter phone number';
        } else if (!PhoneNo.test(fieldValue)) {
          errors.phone = 'Phone number must be 10 digits';
        } else {
          errors.phone = '';
        }
        break;
      case 'password':
        errors.password = getPasswordValidationError(fieldValue);
        break;
      case 'confirmpassword':
        if (fieldValue === '') {
          errors.confirmpassword = 'Please re-enter password';
        } else if (fieldValue !== registerValues.password) {
          errors.confirmpassword = 'Passwords do not match';
        } else {
          errors.confirmpassword = '';
        }
        break;
      default:
        break;
    }

    setRegisterValues((prev) => ({
      ...prev,
      [name]: fieldValue,
      errors,
    }));
  };

  const handleUserLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    let errors = { ...loginValues.errors };

    if (loginValues.email === '') {
      errors.email = 'Please enter email';
    } else if (!EmailReg.test(loginValues.email)) {
      errors.email = 'Please enter valid email';
    }

    if (loginValues.password === '') {
      errors.password = 'Please enter password';
    }

    if (errors.email || errors.password) {
      setLoginValues((prev) => ({ ...prev, errors }));
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        username: loginValues.email.toLowerCase(),
        password: loginValues.password,
      };

      const response = await axios.post(
        BASE_URL_SANDBOX + LOGIN_CUSTOMER_NEW,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response?.data?.status === 200) {
        UseCookieStorage(
          'userLoginData',
          JSON.stringify(response?.data?.record),
          new Date(new Date().getTime() + 2 * 60 * 60 * 1000)
        );
        UseCookieStorage(
          'login_token',
          response?.data?.token,
          new Date(response?.data?.token_expires_at)
        );
        handleLoginSuccess(response?.data?.record);
      } else {
        setErrorMessage(response?.data?.message || 'Invalid email or password');
      }
    } catch (error) {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    let errors = { ...registerValues.errors };
    let hasError = false;

    if (registerValues.firstname === '') {
      errors.firstname = 'Please enter First Name';
      hasError = true;
    }
    if (registerValues.email === '') {
      errors.email = 'Please enter email';
      hasError = true;
    } else if (!EmailReg.test(registerValues.email)) {
      errors.email = 'Please enter valid email';
      hasError = true;
    }
    if (registerValues.phone === '') {
      errors.phone = 'Please enter phone number';
      hasError = true;
    } else if (!PhoneNo.test(registerValues.phone)) {
      errors.phone = 'Phone number must be 10 digits';
      hasError = true;
    }
    const passwordError = getPasswordValidationError(registerValues.password);
    if (passwordError) {
      errors.password = passwordError;
      hasError = true;
    }
    if (registerValues.confirmpassword === '') {
      errors.confirmpassword = 'Please re-enter password';
      hasError = true;
    } else if (registerValues.confirmpassword !== registerValues.password) {
      errors.confirmpassword = 'Passwords do not match';
      hasError = true;
    }

    // Validate address is selected
    if (!selectedAddress) {
      setAddressError('Please select an address from the suggestions.');
      hasError = true;
    }

    // Validate reCAPTCHA
    if (!recaptchaValue) {
      setRecaptchaError('Please verify you are not a robot');
      hasError = true;
    }

    if (hasError) {
      setRegisterValues((prev) => ({ ...prev, errors }));
      setIsLoading(false);
      return;
    }

    try {
      const data = {
        firstname: registerValues.firstname.trim(),
        lastname: registerValues.lastname.trim(),
        email: registerValues.email.toLowerCase(),
        phone: registerValues.phone,
        password: registerValues.password,
        guest: 'No',
        ip_address: '',
        merchant_id: '',
      };

      const response = await axios.post(
        BASE_URL_SANDBOX + REGISTER_CUSTOMER_NEW,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response?.data?.status === 200) {
        UseCookieStorage(
          'userLoginData',
          JSON.stringify(response?.data?.result),
          new Date(new Date().getTime() + 2 * 60 * 60 * 1000)
        );
        UseCookieStorage(
          'login_token',
          response?.data?.token,
          new Date(response?.data?.token_expires_at)
        );
        // Save address to backend and cookies on successful registration
        if (selectedAddress) {
          await saveAddressToBackend(selectedAddress, response?.data?.result);
        }
        handleLoginSuccess(response?.data?.result);
      } else {
        setErrorMessage(response?.data?.message || 'Registration failed');
      }
    } catch (error) {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has addresses
  const checkUserHasAddresses = async (userId) => {
    try {
      const response = await axios.post(
        BASE_URL_SANDBOX + 'Customer_Account/get_customer_account_info',
        { id: userId, merchant_id: '' },
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.status === 200 && Array.isArray(response.data?.address)) {
        return response.data.address.length > 0;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Complete Google login after address is set
  const completeGoogleLoginWithAddress = async () => {
    if (!pendingGoogleUser || !selectedAddress) return;
    
    setIsLoading(true);
    try {
      // Save address to backend
      await saveAddressToBackend(selectedAddress, pendingGoogleUser);
      
      // Complete login
      handleLoginSuccess(pendingGoogleUser);
      setPendingGoogleUser(null);
    } catch (error) {
      setErrorMessage('Failed to save address. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (response) => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: {
              Authorization: `Bearer ${response.access_token}`,
            },
          }
        );

        if (res.status === 200) {
          const data = {
            given_name: res.data.given_name,
            family_name: res.data.family_name,
            email: res.data.email,
            auth_user_id: res.data.sub,
            // The server verifies this with Google; the fields above are display-only.
            access_token: response.access_token,
          };

          const loginResponse = await axios.post(
            BASE_URL_SANDBOX + GOOGLE_LOGIN_CUSTOMER,
            data,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );

          if (loginResponse?.data?.status === 200) {
            const userData = loginResponse?.data?.result;
            
            UseCookieStorage(
              'userLoginData',
              JSON.stringify(userData),
              new Date(new Date().getTime() + 2 * 60 * 60 * 1000)
            );
            UseCookieStorage(
              'login_token',
              loginResponse?.data?.token,
              new Date(loginResponse?.data?.token_expires_at)
            );
            
            // Go directly to home after Google login
            handleLoginSuccess(userData);
          } else {
            setErrorMessage('Google login failed. Please try again.');
          }
        }
      } catch (error) {
        setErrorMessage('Google login failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setErrorMessage('Google login was cancelled.');
    },
  });

  const resetForms = () => {
    setLoginValues({
      email: '',
      password: '',
      errors: { email: '', password: '' },
    });
    setRegisterValues({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      password: '',
      confirmpassword: '',
      errors: {
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        password: '',
        confirmpassword: '',
      },
    });
    setErrorMessage('');
    // Reset address state
    setAddressSearchInput('');
    setAddressPlaces([]);
    setSelectedAddress(null);
    setAddressError('');
    // Reset reCAPTCHA state
    setRecaptchaValue(null);
    setRecaptchaError('');
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    if (value) {
      setRecaptchaError('');
    }
  };

  return {
    isLoading,
    errorMessage,
    setErrorMessage,
    loginValues,
    registerValues,
    handleLoginInputChange,
    handleRegisterInputChange,
    handleUserLogin,
    handleUserRegister,
    loginWithGoogle,
    resetForms,
    // Address autocomplete exports
    addressSearchInput,
    addressPlaces,
    addressLoading,
    selectedAddress,
    addressError,
    setAddressError,
    handleAddressInputChange,
    handleAddressPlaceSelect,
    setAddressPlaces,
    // Google login with address
    pendingGoogleUser,
    completeGoogleLoginWithAddress,
    // reCAPTCHA
    recaptchaValue,
    recaptchaError,
    handleRecaptchaChange,
  };
};

export default useAuthModalLogic;
